import { CaseHeader } from '@/models/case-header.model';
import { CaseImage } from '@/models/case-image.model';
import { CaseLine } from '@/models/case-line.model';
import { Case } from '@/models/case.model';
import { DefectReason } from '@/models/defect-reason.model';
import { FactoryCode } from '@/models/factory-code.model';
import { ProductionCode } from '@/models/production-code.model';
import { ValidatorResult } from '@/models/validation-result.model';
import { CaseDto, CaseType, CaseUpdateDto, CaseValidateDto } from '@onrunning/backstage-models';
import { CrudService } from './crud.service';

export class CasesService {
  private crudService: CrudService<Case>;

  constructor() {
    this.crudService = new CrudService<Case>('api/v2/cases');
  }

  public async get(id: string, accountId: string): Promise<ValidatorResult<CaseDto>> {
    return await this.crudService.do('get', `${id}?accountId=${accountId}`);
  }

  public async getCasesHeaders(caseType: CaseType, accountId: string): Promise<ValidatorResult<CaseHeader[]>> {
    return await this.crudService.do('get', `?caseType=${caseType}&accountId=${accountId}`);
  }

  public create(caseOrder: Case, accountId: string): Promise<ValidatorResult<Case>> {
    return this.crudService.post(caseOrder, { params: { accountId } });
  }

  public update(id: string, caseUpdate: CaseUpdateDto, accountId: string): Promise<ValidatorResult<Case>> {
    return this.crudService.do('put', `${id}?accountId=${accountId}`, caseUpdate);
  }

  public async validate(id: string, caseOrder: CaseValidateDto, accountId: string): Promise<ValidatorResult<void>> {
    return await this.crudService.do('post', `${id}/validate?accountId=${accountId}`, caseOrder);
  }

  public async remove(id: string, accountId: string): Promise<ValidatorResult<Case>> {
    return await this.crudService.remove(id, { params: { accountId } });
  }

  public async submit(id: string, accountId: string): Promise<ValidatorResult<Case>> {
    return await this.crudService.do('put', `submit/${id}?accountId=${accountId}`);
  }

  public async getDefectReasons(caseType: string, countryId: string): Promise<ValidatorResult<DefectReason[]>> {
    return await this.crudService.do('get', `defect-reasons/${caseType}?countryId=${countryId}`);
  }

  public async getFactoryCodes(): Promise<ValidatorResult<FactoryCode[]>> {
    return await this.crudService.do('get', 'factory-codes');
  }

  public async getProductionCodes(): Promise<ValidatorResult<ProductionCode[]>> {
    return await this.crudService.do('get', 'production-codes');
  }

  public async updateLine(caseId: string, line: CaseLine, accountId: string): Promise<ValidatorResult<CaseLine>> {
    return await this.crudService.do('patch', `${caseId}/lines/${line.id}?accountId=${accountId}`, line);
  }

  public async deleteLine(caseId: string, line: CaseLine, accountId: string): Promise<ValidatorResult<CaseLine>> {
    return await this.crudService.do('delete', `${caseId}/lines/${line.id}?accountId=${accountId}`);
  }

  public async addLineImage(
    caseId: string,
    line: CaseLine,
    imageFile: File,
    accountId: string,
  ): Promise<ValidatorResult<CaseImage>> {
    const formData = new FormData();
    formData.append('file', imageFile);

    return await this.crudService.do('post', `${caseId}/lines/${line.id}/add-image?accountId=${accountId}`, formData, {
      'Content-Type': 'multipart/form-data',
    });
  }

  public async deleteLineImage(
    caseId: string,
    line: CaseLine,
    imageId: string,
    accountId: string,
  ): Promise<ValidatorResult<CaseLine>> {
    return await this.crudService.do(
      'delete',
      `${caseId}/lines/${line.id}/remove-image/${imageId}?accountId=${accountId}`,
    );
  }
}
